/**
 * MemoryTab Component
 *
 * Dedicated tab for memory system configuration.
 * Groups all memory-related settings:
 * - Provider and model selection
 * - Agentic memory toggle
 * - Memory model sampling parameters
 * - Custom prompts (Mem0)
 * - Custom categories (Mem0)
 * - Query settings
 * - Ebbinghaus decay settings
 */

import React, { useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { FieldSection } from '@/components/fields/FieldSection';
import { FIELD_CONFIG } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import type { AgentConfiguration } from '@/types';

// Memory-related sections in display order
const MEMORY_SECTIONS = [
  'memory',
  'memory_sampling',
  'memory_prompts',
  'memory_query',
  'memory_ebbinghaus',
];

interface MemoryTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const MemoryTab: React.FC<MemoryTabProps> = ({
  config: _config, // Kept for interface consistency
  onChange,
  appContext,
}) => {
  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (MEMORY_SECTIONS.includes(section)) {
        if (!groups[section]) {
          groups[section] = [];
        }
        groups[section].push({ name, schema });
      }
    });

    return groups;
  }, []);

  // Check if a section has any visible fields
  const sectionHasVisibleFields = useCallback((sectionKey: string) => {
    const fields = groupedFields[sectionKey];
    if (!fields || fields.length === 0) return false;

    return fields.some((field) => {
      if (!field.schema.showIf) return true;
      return field.schema.showIf(appContext);
    });
  }, [groupedFields, appContext]);

  // Sort sections by MEMORY_SECTIONS order
  const sortedSections = useMemo(() => {
    return MEMORY_SECTIONS
      .filter(key => FIELD_SECTIONS[key] && groupedFields[key]?.length > 0)
      .map(key => [key, FIELD_SECTIONS[key]] as [string, typeof FIELD_SECTIONS[string]]);
  }, [groupedFields]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">记忆系统配置</h2>
          <p className="text-sm text-slate-500 mt-1">
            配置长期记忆存储、检索和管理参数
          </p>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sortedSections.map(([sectionKey, sectionConfig]) => {
          if (!sectionHasVisibleFields(sectionKey)) {
            return null;
          }
          return (
            <div
              key={sectionKey}
              className={sectionConfig.layout === 1 ? 'xl:col-span-2' : ''}
            >
              <FieldSection
                sectionKey={sectionKey}
                sectionConfig={sectionConfig}
                fields={groupedFields[sectionKey] || []}
                values={appContext}
                onChange={onChange}
                appContext={appContext}
              />
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <Card className="p-4 bg-blue-50 border-blue-100">
        <h4 className="text-sm font-medium text-blue-800 mb-2">记忆系统说明</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• <strong>MEM0 Provider</strong>: 高级记忆管理，支持自定义分类和遗忘曲线</li>
          <li>• <strong>PostgreSQL Provider</strong>: 简单的向量存储记忆</li>
          <li>• <strong>Agentic Memory</strong>: 启用后Agent可以主动管理自己的记忆</li>
          <li>• <strong>Memory Decay</strong>: 基于艾宾浩斯遗忘曲线的记忆衰减机制</li>
        </ul>
      </Card>
    </div>
  );
};

export default MemoryTab;
